import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Wrench, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api("/auth/password-reset/confirm", { method: "POST", body: JSON.stringify({ token, password }) });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (e2) {
      setError(e2.message || "Invalid or expired reset token");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-midnight to-midnight-2 px-6 py-12">
      <div className="w-full max-w-md rounded-2xl bg-card p-8 shadow-xl">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-midnight"><Wrench className="h-4 w-4 text-brand" /></div>
          <span className="font-display text-lg font-bold tracking-widest text-midnight">PRO GARAGES</span>
        </div>
        {!token ? (
          <p className="text-sm text-danger">Missing reset token. Please use the link from your email.</p>
        ) : done ? (
          <div className="text-center">
            <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-success" />
            <h1 className="font-display text-2xl font-bold uppercase text-midnight">Password reset</h1>
            <p className="mt-2 text-sm text-muted-foreground">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold uppercase text-midnight">Choose a new password</h1>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">NEW PASSWORD</span>
                <input data-testid="new-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  className="w-full rounded-lg border border-input bg-card px-3.5 py-3 text-base outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">CONFIRM PASSWORD</span>
                <input data-testid="confirm-password-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••"
                  className="w-full rounded-lg border border-input bg-card px-3.5 py-3 text-base outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
              </label>
              {error && <p className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger">{error}</p>}
              <button type="submit" disabled={loading} data-testid="reset-submit-btn" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3.5 text-sm font-extrabold tracking-widest text-white disabled:opacity-60">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "SET NEW PASSWORD"}
              </button>
            </form>
          </>
        )}
        <Link to="/login" className="mt-5 block text-center text-sm font-semibold text-brand-2">Back to sign in</Link>
      </div>
    </div>
  );
}
